/*
 * Copyright © 2026 MS IMAGES. All rights reserved.
 * Proprietary and confidential — unauthorized copying, distribution, or use
 * of this file, via any medium, is strictly prohibited. See LICENSE.
 */

/**
 * ============================================================================
 * Ledger Accounts Page - SuperAdmin Portal
 * ============================================================================
 *
 * Displays the Ledger Accounts Registry for a selected organization.
 * SuperAdmins can browse, search, and filter ledger accounts to audit
 * the chart of accounts across tenants.
 *
 * ============================================================================
 */

import { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  BookOpen,
  RefreshCw,
  Search,
  Filter,
  ChevronDown,
  Database,
  CheckCircle2,
  Layers,
} from 'lucide-react';
import { useLedgerAccounts } from '@/hooks/api/useJournalLedger';
import { useOrganizations } from '@/hooks/api/useOrganizations';

// ============================================================================
// Constants
// ============================================================================

const ACCOUNT_TYPES = [
  'CASH_REGISTER',
  'BANK_ACCOUNT',
  'MOBILE_WALLET',
  'PARTNER_WALLET',
  'ORG_REVENUE',
  'ORG_COGS',
  'ORG_EXPENSE',
  'OPENING_BALANCE',
  'SUSPENSE',
  'FX_GAIN_LOSS',
  'INVOICE',
  'PURCHASE_ORDER',
  'SALES_ORDER',
  'CREDIT_NOTE',
  'DEBIT_NOTE',
] as const;

type AccountType = (typeof ACCOUNT_TYPES)[number];

/**
 * Maps each account type to a CSS class suffix for badge coloring.
 * The actual colors are defined in the page styles below using CoreX CSS variables.
 */
const ACCOUNT_TYPE_COLOR_MAP: Record<AccountType, string> = {
  CASH_REGISTER: 'blue',
  BANK_ACCOUNT: 'indigo',
  MOBILE_WALLET: 'purple',
  PARTNER_WALLET: 'amber',
  ORG_REVENUE: 'green',
  ORG_COGS: 'red',
  ORG_EXPENSE: 'orange',
  OPENING_BALANCE: 'grey',
  SUSPENSE: 'yellow',
  FX_GAIN_LOSS: 'cyan',
  INVOICE: 'teal',
  PURCHASE_ORDER: 'pink',
  SALES_ORDER: 'violet',
  CREDIT_NOTE: 'lime',
  DEBIT_NOTE: 'rose',
};

function getTypeBadgeClass(accountType: string): string {
  const color = ACCOUNT_TYPE_COLOR_MAP[accountType as AccountType] ?? 'grey';
  return `la-type-badge la-type-${color}`;
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return iso;
  }
}

// ============================================================================
// Main Page Component
// ============================================================================

export function LedgerAccountsPage() {
  const { t } = useTranslation('platform');

  // ---- State ----
  const [selectedOrgId, setSelectedOrgId] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [showInactive, setShowInactive] = useState(false);

  // ---- Data ----
  const {
    data: orgsResponse,
    isLoading: orgsLoading,
  } = useOrganizations({ page: 0, size: 100 });

  const orgs = orgsResponse?.content ?? [];

  const {
    data: accounts = [],
    isLoading: accountsLoading,
    refetch,
  } = useLedgerAccounts(selectedOrgId);

  // ---- Filtering ----
  const filteredAccounts = useMemo(() => {
    let result = accounts;

    // Filter by active/inactive
    if (!showInactive) {
      result = result.filter((a) => a.active);
    }

    // Filter by account type
    if (typeFilter) {
      result = result.filter((a) => a.accountType === typeFilter);
    }

    // Filter by search query (code or name)
    if (searchQuery.trim()) {
      const q = searchQuery.trim().toLowerCase();
      result = result.filter(
        (a) =>
          a.code.toLowerCase().includes(q) ||
          a.name.toLowerCase().includes(q) ||
          (a.referenceId && a.referenceId.toLowerCase().includes(q))
      );
    }

    return result;
  }, [accounts, showInactive, typeFilter, searchQuery]);

  // ---- Stats ----
  const stats = useMemo(() => {
    const total = accounts.length;
    const active = accounts.filter((a) => a.active).length;

    const typeBreakdown: Record<string, number> = {};
    for (const account of accounts) {
      typeBreakdown[account.accountType] =
        (typeBreakdown[account.accountType] || 0) + 1;
    }

    return { total, active, typeBreakdown };
  }, [accounts]);

  const typeBreakdownEntries = useMemo(
    () =>
      Object.entries(stats.typeBreakdown).sort(([, a], [, b]) => b - a),
    [stats.typeBreakdown]
  );

  // ---- Org change handler ----
  const handleOrgChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSelectedOrgId(e.target.value);
    setSearchQuery('');
    setTypeFilter('');
  };

  // ---- Loading state ----
  const isLoading = orgsLoading || (selectedOrgId && accountsLoading);

  // ---- Render ----
  return (
    <div className="la-page">
      {/* ====== Header ====== */}
      <div className="la-header">
        <div className="la-header-text">
          <h1 className="la-title">
            <BookOpen size={22} />
            {t('platform:journal.accounts.title', 'Ledger Accounts Registry')}
          </h1>
          <p className="la-subtitle">
            {t(
              'platform:journal.accounts.subtitle',
              'Browse and audit ledger accounts across organizations'
            )}
          </p>
        </div>
        <div className="la-header-actions">
          {selectedOrgId && (
            <button
              className="la-btn la-btn-secondary"
              onClick={() => refetch()}
              disabled={accountsLoading}
              title={t('platform:journal.accounts.refresh', 'Refresh')}
            >
              <RefreshCw
                size={15}
                className={accountsLoading ? 'la-spinner' : ''}
              />
            </button>
          )}
        </div>
      </div>

      {/* ====== Organization Selector ====== */}
      <div className="la-org-selector-card">
        <label className="la-org-label" htmlFor="la-org-select">
          {t('platform:journal.accounts.selectOrg', 'Organization')}
        </label>
        <div className="la-org-select-wrap">
          <select
            id="la-org-select"
            className="la-org-select"
            value={selectedOrgId}
            onChange={handleOrgChange}
            disabled={orgsLoading}
          >
            <option value="">
              {orgsLoading
                ? t('platform:journal.accounts.loadingOrgs', 'Loading organizations...')
                : t(
                    'platform:journal.accounts.selectOrgPlaceholder',
                    'Select an organization'
                  )}
            </option>
            {orgs.map((org) => (
              <option key={org.id} value={org.id}>
                {org.name} ({org.code})
              </option>
            ))}
          </select>
          <ChevronDown size={16} className="la-org-select-icon" />
        </div>
      </div>

      {/* ====== Content Area ====== */}
      {!selectedOrgId ? (
        <div className="la-empty-state">
          <Database size={48} />
          <p>
            {t(
              'platform:journal.accounts.noOrgSelected',
              'Select an organization above to view its ledger accounts'
            )}
          </p>
        </div>
      ) : isLoading ? (
        <div className="la-loading">
          <RefreshCw className="la-spinner" size={24} />
          <span>
            {t('platform:journal.accounts.loading', 'Loading accounts...')}
          </span>
        </div>
      ) : (
        <>
          {/* ====== Stats Row ====== */}
          <div className="la-stats-row">
            <div className="la-stat-card">
              <div className="la-stat-icon la-stat-icon-total">
                <Database size={18} />
              </div>
              <div className="la-stat-content">
                <span className="la-stat-value">{stats.total}</span>
                <span className="la-stat-label">
                  {t(
                    'platform:journal.accounts.stats.total',
                    'Total Accounts'
                  )}
                </span>
              </div>
            </div>

            <div className="la-stat-card">
              <div className="la-stat-icon la-stat-icon-active">
                <CheckCircle2 size={18} />
              </div>
              <div className="la-stat-content">
                <span className="la-stat-value">{stats.active}</span>
                <span className="la-stat-label">
                  {t(
                    'platform:journal.accounts.stats.active',
                    'Active Accounts'
                  )}
                </span>
              </div>
            </div>

            <div className="la-stat-card">
              <div className="la-stat-icon la-stat-icon-types">
                <Layers size={18} />
              </div>
              <div className="la-stat-content">
                <span className="la-stat-value">
                  {Object.keys(stats.typeBreakdown).length}
                </span>
                <span className="la-stat-label">
                  {t(
                    'platform:journal.accounts.stats.types',
                    'Account Types'
                  )}
                </span>
              </div>
            </div>

            {/* Type breakdown mini-badges */}
            {typeBreakdownEntries.length > 0 && (
              <div className="la-stat-card la-stat-card-wide">
                <div className="la-stat-icon la-stat-icon-breakdown">
                  <Filter size={18} />
                </div>
                <div className="la-stat-breakdown">
                  <span className="la-stat-label">
                    {t(
                      'platform:journal.accounts.stats.breakdown',
                      'Type Breakdown'
                    )}
                  </span>
                  <div className="la-breakdown-badges">
                    {typeBreakdownEntries.map(([type, count]) => (
                      <span
                        key={type}
                        className={getTypeBadgeClass(type)}
                        title={type}
                      >
                        {type.replace(/_/g, ' ')} ({count})
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* ====== Filters ====== */}
          <div className="la-filters">
            <div className="la-search-wrap">
              <Search size={15} className="la-search-icon" />
              <input
                type="text"
                className="la-search-input"
                placeholder={t(
                  'platform:journal.accounts.searchPlaceholder',
                  'Search by code, name, or reference...'
                )}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </div>

            <div className="la-filter-type-wrap">
              <select
                className="la-filter-select"
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
              >
                <option value="">
                  {t(
                    'platform:journal.accounts.allTypes',
                    'All Account Types'
                  )}
                </option>
                {ACCOUNT_TYPES.map((type) => (
                  <option key={type} value={type}>
                    {type.replace(/_/g, ' ')}
                  </option>
                ))}
              </select>
              <ChevronDown size={14} className="la-filter-select-icon" />
            </div>

            <label className="la-toggle-label">
              <input
                type="checkbox"
                className="la-toggle-input"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <span className="la-toggle-switch" />
              <span className="la-toggle-text">
                {t(
                  'platform:journal.accounts.showInactive',
                  'Show Inactive'
                )}
              </span>
            </label>
          </div>

          {/* ====== Table ====== */}
          <div className="la-card">
            {filteredAccounts.length === 0 ? (
              <div className="la-empty">
                <Database size={40} />
                <p>
                  {searchQuery || typeFilter || showInactive
                    ? t(
                        'platform:journal.accounts.noResults',
                        'No accounts match your filters'
                      )
                    : t(
                        'platform:journal.accounts.empty',
                        'No ledger accounts found for this organization'
                      )}
                </p>
              </div>
            ) : (
              <div className="la-table-wrap">
                <table className="la-table">
                  <thead>
                    <tr>
                      <th>
                        {t(
                          'platform:journal.accounts.table.code',
                          'Code'
                        )}
                      </th>
                      <th>
                        {t(
                          'platform:journal.accounts.table.name',
                          'Name'
                        )}
                      </th>
                      <th>
                        {t(
                          'platform:journal.accounts.table.type',
                          'Account Type'
                        )}
                      </th>
                      <th>
                        {t(
                          'platform:journal.accounts.table.referenceId',
                          'Reference ID'
                        )}
                      </th>
                      <th>
                        {t(
                          'platform:journal.accounts.table.active',
                          'Active'
                        )}
                      </th>
                      <th>
                        {t(
                          'platform:journal.accounts.table.createdAt',
                          'Created At'
                        )}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredAccounts.map((account) => (
                      <tr
                        key={account.id}
                        className={account.active ? '' : 'la-row-inactive'}
                      >
                        <td>
                          <span className="la-code">{account.code}</span>
                        </td>
                        <td>
                          <span className="la-account-name">
                            {account.name}
                          </span>
                        </td>
                        <td>
                          <span className={getTypeBadgeClass(account.accountType)}>
                            {account.accountType.replace(/_/g, ' ')}
                          </span>
                        </td>
                        <td>
                          {account.referenceId ? (
                            <span className="la-ref-id">
                              {account.referenceId}
                            </span>
                          ) : (
                            <span className="la-text-muted">--</span>
                          )}
                        </td>
                        <td>
                          <span
                            className={`la-active-dot ${
                              account.active
                                ? 'la-active-dot-on'
                                : 'la-active-dot-off'
                            }`}
                            title={
                              account.active
                                ? t(
                                    'platform:journal.accounts.active',
                                    'Active'
                                  )
                                : t(
                                    'platform:journal.accounts.inactive',
                                    'Inactive'
                                  )
                            }
                          />
                        </td>
                        <td>
                          <span className="la-date">
                            {formatDate(account.createdAt)}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Results count */}
            {filteredAccounts.length > 0 && (
              <div className="la-results-footer">
                <span className="la-results-count">
                  {t(
                    'platform:journal.accounts.resultsCount',
                    '{{count}} account(s)',
                    { count: filteredAccounts.length }
                  )}
                  {filteredAccounts.length !== accounts.length && (
                    <>
                      {' '}
                      {t(
                        'platform:journal.accounts.outOf',
                        'of {{total}} total',
                        { total: accounts.length }
                      )}
                    </>
                  )}
                </span>
              </div>
            )}
          </div>
        </>
      )}

      <style>{pageStyles}</style>
    </div>
  );
}

// ============================================================================
// Styles
// ============================================================================

const pageStyles = `
  /* ---- Page Layout ---- */
  .la-page {
    display: flex;
    flex-direction: column;
    gap: 1.25rem;
    padding: 1.5rem 2rem;
    width: 100%;
    box-sizing: border-box;
  }

  /* ---- Header ---- */
  .la-header {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 1rem;
    animation: la-header-in 0.4s cubic-bezier(0.22,1,0.36,1) both;
  }

  .la-header-text {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .la-title {
    font-family: var(--font-heading);
    font-size: 1.5rem;
    font-weight: 700;
    color: var(--color-text-primary);
    display: flex;
    align-items: center;
    gap: 0.5rem;
    margin: 0;
  }

  .la-subtitle {
    font-family: var(--font-body);
    font-size: 0.875rem;
    color: var(--color-text-secondary);
    margin: 0;
  }

  .la-header-actions {
    display: flex;
    gap: 0.5rem;
    flex-shrink: 0;
  }

  /* ---- Buttons ---- */
  .la-btn {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 1rem;
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.15s ease;
    border: none;
    white-space: nowrap;
  }

  .la-btn-secondary {
    background: var(--color-bg-secondary);
    color: var(--color-text-primary);
    border: 1px solid var(--color-border-default);
  }

  .la-btn-secondary:hover:not(:disabled) {
    background: var(--color-bg-tertiary);
  }

  .la-btn-secondary:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  /* ---- Org Selector Card ---- */
  .la-org-selector-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    padding: 1rem 1.25rem;
    display: flex;
    align-items: center;
    gap: 1rem;
    animation: la-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.04s both;
  }

  .la-org-label {
    font-family: var(--font-body);
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--color-text-secondary);
    white-space: nowrap;
  }

  .la-org-select-wrap {
    position: relative;
    flex: 1;
    max-width: 480px;
  }

  .la-org-select {
    width: 100%;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    border: 1px solid var(--input-border);
    background: var(--input-bg);
    color: var(--color-text-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: 0.8125rem;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .la-org-select:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--input-focus-ring);
  }

  .la-org-select:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }

  .la-org-select-icon {
    position: absolute;
    right: 0.625rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }

  /* ---- Stats Row ---- */
  .la-stats-row {
    display: flex;
    gap: 0.75rem;
    flex-wrap: wrap;
    animation: la-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.08s both;
  }

  .la-stat-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    padding: 0.875rem 1.125rem;
    display: flex;
    align-items: center;
    gap: 0.75rem;
    min-width: 160px;
  }

  .la-stat-card-wide {
    flex: 1;
    min-width: 280px;
    align-items: flex-start;
  }

  .la-stat-icon {
    width: 36px;
    height: 36px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: var(--radius-md);
    flex-shrink: 0;
  }

  .la-stat-icon-total {
    background: var(--color-info-light);
    color: var(--color-info);
  }

  .la-stat-icon-active {
    background: var(--color-success-light);
    color: var(--color-success);
  }

  .la-stat-icon-types {
    background: var(--color-warning-light);
    color: var(--color-warning);
  }

  .la-stat-icon-breakdown {
    background: var(--color-bg-tertiary);
    color: var(--color-text-secondary);
  }

  .la-stat-content {
    display: flex;
    flex-direction: column;
    gap: 0.125rem;
  }

  .la-stat-value {
    font-family: var(--font-heading);
    font-size: 1.25rem;
    font-weight: 700;
    color: var(--color-text-primary);
    line-height: 1;
  }

  .la-stat-label {
    font-family: var(--font-body);
    font-size: 0.6875rem;
    font-weight: 500;
    color: var(--color-text-muted);
    text-transform: uppercase;
    letter-spacing: 0.03em;
  }

  .la-stat-breakdown {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    min-width: 0;
  }

  .la-breakdown-badges {
    display: flex;
    flex-wrap: wrap;
    gap: 0.375rem;
  }

  /* ---- Filters ---- */
  .la-filters {
    display: flex;
    gap: 0.75rem;
    align-items: center;
    flex-wrap: wrap;
    animation: la-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.12s both;
  }

  .la-search-wrap {
    position: relative;
    flex: 1;
    min-width: 200px;
    max-width: 360px;
  }

  .la-search-icon {
    position: absolute;
    left: 0.75rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }

  .la-search-input {
    width: 100%;
    padding: 0.5rem 0.75rem 0.5rem 2.25rem;
    border: 1px solid var(--input-border);
    background: var(--input-bg);
    color: var(--color-text-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: 0.8125rem;
    box-sizing: border-box;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .la-search-input:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--input-focus-ring);
  }

  .la-search-input::placeholder {
    color: var(--color-text-muted);
  }

  .la-filter-type-wrap {
    position: relative;
    min-width: 180px;
  }

  .la-filter-select {
    width: 100%;
    padding: 0.5rem 2rem 0.5rem 0.75rem;
    border: 1px solid var(--input-border);
    background: var(--input-bg);
    color: var(--color-text-primary);
    border-radius: var(--radius-md);
    font-family: var(--font-body);
    font-size: 0.8125rem;
    appearance: none;
    cursor: pointer;
    transition: border-color 0.15s ease, box-shadow 0.15s ease;
  }

  .la-filter-select:focus {
    outline: none;
    border-color: var(--color-brand-primary);
    box-shadow: 0 0 0 2px var(--input-focus-ring);
  }

  .la-filter-select-icon {
    position: absolute;
    right: 0.625rem;
    top: 50%;
    transform: translateY(-50%);
    color: var(--color-text-muted);
    pointer-events: none;
  }

  /* ---- Toggle ---- */
  .la-toggle-label {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    cursor: pointer;
    font-family: var(--font-body);
    font-size: 0.8125rem;
    color: var(--color-text-primary);
    white-space: nowrap;
    user-select: none;
  }

  .la-toggle-input {
    position: absolute;
    opacity: 0;
    width: 0;
    height: 0;
  }

  .la-toggle-switch {
    position: relative;
    width: 36px;
    height: 20px;
    background: var(--color-border-default);
    border-radius: var(--radius-full);
    transition: background 0.2s ease;
    flex-shrink: 0;
  }

  .la-toggle-switch::after {
    content: '';
    position: absolute;
    top: 2px;
    left: 2px;
    width: 16px;
    height: 16px;
    background: #fff;
    border-radius: var(--radius-full);
    transition: transform 0.2s ease;
  }

  .la-toggle-input:checked + .la-toggle-switch {
    background: var(--color-brand-primary);
  }

  .la-toggle-input:checked + .la-toggle-switch::after {
    transform: translateX(16px);
  }

  .la-toggle-text {
    font-size: 0.8125rem;
    color: var(--color-text-secondary);
  }

  /* ---- Card ---- */
  .la-card {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    overflow: hidden;
    animation: la-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.16s both;
  }

  /* ---- Table ---- */
  .la-table-wrap {
    overflow-x: auto;
  }

  .la-table {
    width: 100%;
    border-collapse: collapse;
    font-family: var(--font-body);
  }

  .la-table th {
    text-align: left;
    padding: 0.75rem 1rem;
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--color-text-secondary);
    background: var(--color-bg-tertiary);
    border-bottom: 1px solid var(--color-border-subtle);
    white-space: nowrap;
  }

  .la-table td {
    padding: 0.75rem 1rem;
    font-size: 0.8125rem;
    color: var(--color-text-primary);
    border-bottom: 1px solid var(--color-border-subtle);
    vertical-align: middle;
  }

  .la-table tbody tr:last-child td {
    border-bottom: none;
  }

  .la-table tbody tr:hover {
    background: var(--color-bg-tertiary);
  }

  .la-row-inactive {
    opacity: 0.55;
  }

  /* ---- Cell elements ---- */
  .la-code {
    font-family: monospace;
    font-size: 0.8125rem;
    font-weight: 600;
    color: var(--color-text-primary);
    background: var(--color-bg-tertiary);
    padding: 0.125rem 0.5rem;
    border-radius: var(--radius-sm);
  }

  .la-account-name {
    font-weight: 500;
    color: var(--color-text-primary);
  }

  .la-ref-id {
    font-family: monospace;
    font-size: 0.75rem;
    color: var(--color-text-muted);
    max-width: 180px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    display: inline-block;
  }

  .la-text-muted {
    color: var(--color-text-muted);
  }

  .la-date {
    font-size: 0.8125rem;
    color: var(--color-text-secondary);
    white-space: nowrap;
  }

  /* ---- Active Status Dot ---- */
  .la-active-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    border-radius: var(--radius-full);
  }

  .la-active-dot-on {
    background: var(--color-success);
    box-shadow: 0 0 0 2px var(--color-success-light);
  }

  .la-active-dot-off {
    background: var(--color-text-muted);
    opacity: 0.4;
  }

  /* ---- Type Badges ---- */
  .la-type-badge {
    display: inline-block;
    padding: 0.125rem 0.5rem;
    border-radius: var(--radius-sm);
    font-size: 0.6875rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.02em;
    white-space: nowrap;
  }

  .la-type-blue {
    background: var(--color-info-light);
    color: var(--color-info);
  }

  .la-type-indigo {
    background: rgba(99, 102, 241, 0.12);
    color: rgb(99, 102, 241);
  }

  .la-type-purple {
    background: rgba(139, 92, 246, 0.12);
    color: rgb(139, 92, 246);
  }

  .la-type-amber {
    background: var(--color-warning-light);
    color: var(--color-warning);
  }

  .la-type-green {
    background: var(--color-success-light);
    color: var(--color-success);
  }

  .la-type-red {
    background: var(--color-error-light);
    color: var(--color-error);
  }

  .la-type-orange {
    background: rgba(249, 115, 22, 0.12);
    color: rgb(249, 115, 22);
  }

  .la-type-grey {
    background: var(--color-bg-tertiary);
    color: var(--color-text-muted);
  }

  .la-type-yellow {
    background: rgba(234, 179, 8, 0.12);
    color: rgb(202, 138, 4);
  }

  .la-type-cyan {
    background: rgba(6, 182, 212, 0.12);
    color: rgb(6, 182, 212);
  }

  .la-type-teal {
    background: rgba(20, 184, 166, 0.12);
    color: rgb(20, 184, 166);
  }

  .la-type-pink {
    background: rgba(236, 72, 153, 0.12);
    color: rgb(236, 72, 153);
  }

  .la-type-violet {
    background: rgba(139, 92, 246, 0.12);
    color: rgb(124, 58, 237);
  }

  .la-type-lime {
    background: rgba(132, 204, 22, 0.12);
    color: rgb(101, 163, 13);
  }

  .la-type-rose {
    background: rgba(244, 63, 94, 0.12);
    color: rgb(244, 63, 94);
  }

  /* ---- Results Footer ---- */
  .la-results-footer {
    display: flex;
    align-items: center;
    padding: 0.75rem 1rem;
    border-top: 1px solid var(--color-border-subtle);
  }

  .la-results-count {
    font-family: var(--font-body);
    font-size: 0.75rem;
    color: var(--color-text-muted);
  }

  /* ---- Empty State ---- */
  .la-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 3.5rem 1rem;
    color: var(--color-text-muted);
    font-family: var(--font-body);
    font-size: 0.875rem;
  }

  .la-empty-state {
    background: var(--color-bg-secondary);
    border: 1px solid var(--color-border-subtle);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-sm);
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    padding: 4rem 2rem;
    color: var(--color-text-muted);
    font-family: var(--font-body);
    font-size: 0.9375rem;
    animation: la-card-in 0.4s cubic-bezier(0.22,1,0.36,1) 0.06s both;
  }

  /* ---- Loading ---- */
  .la-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 0.75rem;
    padding: 4rem;
    color: var(--color-text-secondary);
    font-family: var(--font-body);
  }

  .la-spinner {
    animation: la-spin 1s linear infinite;
  }

  /* ---- Animations ---- */
  @keyframes la-header-in {
    from { opacity: 0; transform: translateY(-8px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes la-card-in {
    from { opacity: 0; transform: translateY(14px); }
    to { opacity: 1; transform: translateY(0); }
  }

  @keyframes la-spin {
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  }

  /* ---- Reduced Motion ---- */
  @media (prefers-reduced-motion: reduce) {
    .la-header,
    .la-org-selector-card,
    .la-stats-row,
    .la-filters,
    .la-card,
    .la-empty-state,
    .la-spinner {
      animation: none !important;
    }
  }

  /* ---- Mobile ---- */
  @media (max-width: 640px) {
    .la-page {
      padding: 1rem;
    }

    .la-header {
      flex-direction: column;
      gap: 0.75rem;
    }

    .la-org-selector-card {
      flex-direction: column;
      align-items: stretch;
      gap: 0.5rem;
    }

    .la-org-select-wrap {
      max-width: 100%;
    }

    .la-stats-row {
      flex-direction: column;
    }

    .la-stat-card {
      min-width: 0;
      width: 100%;
    }

    .la-filters {
      flex-direction: column;
      align-items: stretch;
    }

    .la-search-wrap {
      max-width: 100%;
    }

    .la-filter-type-wrap {
      min-width: 0;
      width: 100%;
    }

    .la-toggle-label {
      justify-content: space-between;
      padding: 0.25rem 0;
    }

    .la-breakdown-badges {
      gap: 0.25rem;
    }

    .la-type-badge {
      font-size: 0.625rem;
      padding: 0.0625rem 0.375rem;
    }
  }
`;

export default LedgerAccountsPage;
